import type {ReactElement} from 'react';
import React, {useState} from 'react';
import {ContextualMenuStylingOption} from '@Components/contextual-menu/components/contextual-menu-styling-option';
import type {IconStates} from '@Components/contextual-menu/components/styling-options';
import {Text, TextSize} from '@Components/text';
import {Tooltip, TOOLTIP_POSITION} from '@Components/tooltip';
import {TextStyleOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-styling-options/base-styling-options.types';
import styles from './base-styling-options.module.scss';

interface BaseStylingOptionsProps {
  isBold: boolean;
  isBoldDisabled?: boolean;
  isItalic: boolean;
  lineThrough: boolean;
  underLine: boolean;
  onClickBold(): void;
  onClickItalic(): void;
  onClickLineThrough(): void;
  onClickUnderline(): void;
  tooltipClassName?: string;
  tooltipPosition?: TOOLTIP_POSITION;
}

export function BaseStylingOptions({
  isBoldDisabled = false,
  tooltipClassName = '',
  tooltipPosition = TOOLTIP_POSITION.BOTTOM_MIDDLE,
  ...props
}: BaseStylingOptionsProps): ReactElement<any> {
  const [showBoldDisabledTooltip, setShowBoldDisabledTooltip] = useState<boolean>(false);

  const onClickHandler = (id: TextStyleOptions): void => {
    switch (id) {
      case TextStyleOptions.BOLD:
        return props.onClickBold();
      case TextStyleOptions.ITALIC:
        return props.onClickItalic();
      case TextStyleOptions.STRIKE_THROUGH:
        return props.onClickLineThrough();
      case TextStyleOptions.UNDERLINE:
        return props.onClickUnderline();
      default:
        throw new Error('unhandled text style options');
    }
  };

  const getStylingOptionsData = (): Array<IconStates> => {
    return [
      {
        id: TextStyleOptions.BOLD,
        icon: 'icon-bold',
        isSelected: props.isBold,
        isDisabled: isBoldDisabled,
        onMouseEnter: (): void => {
          if (!showBoldDisabledTooltip) {
            setShowBoldDisabledTooltip(true);
          }
        },
        onMouseLeave: (): void => {
          if (showBoldDisabledTooltip) {
            setShowBoldDisabledTooltip(false);
          }
        },
      },
      {
        id: TextStyleOptions.ITALIC,
        icon: 'icon-italic',
        isSelected: props.isItalic,
      },
      {
        id: TextStyleOptions.UNDERLINE,
        icon: 'icon-underline',
        isSelected: props.underLine,
      },
      {
        id: TextStyleOptions.STRIKE_THROUGH,
        icon: 'icon-strike-through',
        isSelected: props.lineThrough,
      },
    ];
  };

  return (
    <div className={styles.container}>
      <ContextualMenuStylingOption
        className={styles.optionClass}
        id="text-item-styling-option"
        onClick={onClickHandler}
        label={window.i18next.t('pmwjs_style')}
        data={getStylingOptionsData()}
      />
      {showBoldDisabledTooltip && isBoldDisabled ? (
        <Tooltip
          position={tooltipPosition}
          className={`${tooltipClassName}  ${styles.tooltip}`}
          id="bold-text-disabled-tooltip"
          text={<Text val={window.i18next.t('pmwjs_cannot_select_bold')} size={TextSize.XXSMALL} />}
        />
      ) : null}
    </div>
  );
}
